import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import {
  PreferenceFamily,
  resolvePreferenceFamily,
  selectSourceIds,
} from '@/creditflowresearch/domain/Preferences/PreferenceSource';
import {
  MAPPING_EXCEPTIONS,
  SOURCE_ID_MAPPINGS,
  TARGET_FAMILY_CONFIG,
  type MappingTargetFamily,
} from '@/creditflowresearch/domain/Preferences/crossProductMappingConfig';

export type { MappingTargetFamily };

const keepInCanonicalOrder = (ids: string[], canonicalOrder: string[]): string[] => {
  const selected = new Set(ids);

  return canonicalOrder.filter((id) => selected.has(id));
};

const toSelectionKey = (ids: string[], family: MappingTargetFamily): string =>
  keepInCanonicalOrder(ids, TARGET_FAMILY_CONFIG[family].canonicalOrder).join();

const findExceptionTargetIds = (
  sourceFamily: MappingTargetFamily,
  targetFamily: MappingTargetFamily,
  sourceIds: string[],
): string[] | null => {
  const exceptions = MAPPING_EXCEPTIONS[sourceFamily]?.[targetFamily];

  if (!exceptions) {
    return null;
  }

  const selectionKey = toSelectionKey(sourceIds, sourceFamily);
  const exception = exceptions.find(
    (candidate) => toSelectionKey(candidate.sourceIds, sourceFamily) === selectionKey,
  );

  return exception ? [...exception.targetIds] : null;
};

const collectMappedIds = (
  sourceFamily: MappingTargetFamily,
  targetFamily: MappingTargetFamily,
  sourceIds: string[],
): string[] => {
  const selected = new Set(sourceIds);

  return SOURCE_ID_MAPPINGS[sourceFamily]
    .filter(({ id }) => selected.has(id))
    .flatMap(({ mapsTo }) => mapsTo[targetFamily] ?? []);
};

const mapAcrossFamilies = (
  sourceFamily: MappingTargetFamily,
  targetFamily: MappingTargetFamily,
  sourceIds: string[],
): string[] => {
  const exceptionTargetIds = findExceptionTargetIds(sourceFamily, targetFamily, sourceIds);

  if (exceptionTargetIds) {
    return exceptionTargetIds;
  }

  const { canonicalOrder, fallbackWhenNothingMapped } = TARGET_FAMILY_CONFIG[targetFamily];
  const targetIds = keepInCanonicalOrder(
    collectMappedIds(sourceFamily, targetFamily, sourceIds),
    canonicalOrder,
  );

  return targetIds.length ? targetIds : [...fallbackWhenNothingMapped];
};

class CrossProductMappingService {
  static mapAbsToCorporateMarkets(absRegionIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.ABS, PreferenceFamily.CORPORATE, absRegionIds);
  }

  static mapAbsToEmRegions(absRegionIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.ABS, PreferenceFamily.EM, absRegionIds);
  }

  static mapCorporateToAbsRegions(corporateMarketIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.CORPORATE, PreferenceFamily.ABS, corporateMarketIds);
  }

  static mapCorporateToEmRegions(corporateMarketIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.CORPORATE, PreferenceFamily.EM, corporateMarketIds);
  }

  static mapEmToAbsRegions(emRegionIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.EM, PreferenceFamily.ABS, emRegionIds);
  }

  static mapEmToCorporateMarkets(emRegionIds: string[]): string[] {
    return mapAcrossFamilies(PreferenceFamily.EM, PreferenceFamily.CORPORATE, emRegionIds);
  }

  static mapForTarget(
    preferences: Preferences | null,
    targetFamily: MappingTargetFamily,
  ): string[] {
    const sourceFamily = resolvePreferenceFamily(preferences);
    const sourceIds = selectSourceIds(preferences, sourceFamily);

    if (sourceFamily === PreferenceFamily.NONE || !sourceIds.length) {
      return [];
    }

    if (sourceFamily === targetFamily) {
      return keepInCanonicalOrder(
        sourceIds,
        TARGET_FAMILY_CONFIG[targetFamily].canonicalOrder,
      );
    }

    return mapAcrossFamilies(sourceFamily, targetFamily, sourceIds);
  }
}

export default CrossProductMappingService;
